import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Radar, Eye, Type, Contrast, Zap, Globe } from 'lucide-react';
import { useAccessibility } from '../context/AccessibilityContext';

const Navigation = () => {
  const [currentLang, setCurrentLang] = useState('en');
  const [isOpen, setIsOpen] = useState(false);
  const [showA11yPanel, setShowA11yPanel] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const panelRef = useRef(null);
  const langRef = useRef(null);
  const {
    settings,
    toggleAccessibility,
    setFontSize,
    setContrast,
    toggleReducedMotion,
    announce
  } = useAccessibility();

  const changeLanguage = (lang) => {
    if (window.switchLanguage) {
      window.switchLanguage(lang);
    }
    setCurrentLang(lang);
    setShowLangMenu(false);
    announce(`Language changed to ${lang}`);
  };

  useEffect(() => {
    const savedLang = localStorage.getItem('googleTranslateLang') || 'en';
    setCurrentLang(savedLang);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setShowA11yPanel(false);
  }, [location]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowA11yPanel(false);
      }
      if (langRef.current && !langRef.current.contains(e.target)) {
        setShowLangMenu(false);
      }
    };
    if (showA11yPanel || showLangMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showA11yPanel, showLangMenu]);

  // Handle keyboard navigation for panel
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showA11yPanel) {
        setShowA11yPanel(false);
        announce('Accessibility panel closed');
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showA11yPanel, announce]);

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Features', path: '/features' },
    { label: 'Technology', path: '/technology' },
    { label: 'About', path: '/about' },
    { label: 'Impact', path: '/impact' },
    { label: 'Shop', path: '/shop' },
  ];

  return (
    <nav
      data-testid="navigation"
      role="navigation"
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-md border-b border-gray-800/50' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="nav-logo" aria-label="ClearWay Home">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center" aria-hidden="true">
              <Radar className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-semibold tracking-tight">ClearWay</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1" role="menubar">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                role="menuitem"
                aria-current={location.pathname === link.path ? 'page' : undefined}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {/* Language Selector */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-all"
                aria-label="Select language"
                aria-expanded={showLangMenu}
                aria-haspopup="true"
              >
                <Globe className="w-4 h-4" aria-hidden="true" />
                <span className="hidden lg:inline">{currentLang.toUpperCase()}</span>
              </button>

              {/* Language Menu */}
              <AnimatePresence>
                {showLangMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-2 bg-[#0a0a0a] border border-gray-800 rounded-lg shadow-xl z-50 min-w-[180px]"
                  >
                    <button
                      onClick={() => changeLanguage('en')}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${
                        currentLang === 'en'
                          ? 'bg-blue-500/20 text-white'
                          : 'text-gray-300 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      English
                    </button>
                    <button
                      onClick={() => changeLanguage('hi')}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${
                        currentLang === 'hi'
                          ? 'bg-blue-500/20 text-white'
                          : 'text-gray-300 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      हिन्दी
                    </button>
                    <button
                      onClick={() => changeLanguage('mr')}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${
                        currentLang === 'mr'
                          ? 'bg-blue-500/20 text-white'
                          : 'text-gray-300 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      मराठी
                    </button>
                    <button
                      onClick={() => changeLanguage('ar')}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${
                        currentLang === 'ar'
                          ? 'bg-blue-500/20 text-white'
                          : 'text-gray-300 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      العربية
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Accessibility Toggle */}
            <div className="relative" ref={panelRef}>
              <button
                onClick={() => {
                  setShowA11yPanel(!showA11yPanel);
                  announce(showA11yPanel ? 'Accessibility panel closed' : 'Accessibility panel opened');
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium border transition-all ${
                  settings.enabled
                    ? 'bg-blue-500 border-blue-500 text-white'
                    : 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'
                }`}
                aria-label="Accessibility Settings"
                aria-expanded={showA11yPanel}
                aria-haspopup="true"
              >
                <Eye className="w-4 h-4" aria-hidden="true" />
                <span className="hidden lg:inline">Accessibility</span>
              </button>

              {/* Accessibility Panel */}
              <AnimatePresence>
                {showA11yPanel && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="a11y-panel"
                    role="dialog"
                    aria-label="Accessibility settings"
                  >
                    <h3 id="a11y-panel-title">Accessibility Settings</h3>

                    {/* Master toggle */}
                    <div className="a11y-panel-section">
                      <button
                        onClick={toggleAccessibility}
                        className={`a11y-toggle w-full ${settings.enabled ? 'active' : ''}`}
                        aria-pressed={settings.enabled}
                      >
                        <span className="font-medium">Enable Accessibility</span>
                        <div className="a11y-toggle-switch" aria-hidden="true" />
                      </button>
                    </div>

                    {settings.enabled && (
                      <>
                        {/* Font Size */}
                        <div className="a11y-panel-section">
                          <label id="font-size-label">Text Size</label>
                          <div className="a11y-panel-buttons" role="radiogroup" aria-labelledby="font-size-label">
                            {[
                              { value: 'normal', label: 'Normal', icon: 'A' },
                              { value: 'large', label: 'Large', icon: 'A+' },
                              { value: 'xl', label: 'Extra Large', icon: 'A++' }
                            ].map((option) => (
                              <button
                                key={option.value}
                                onClick={() => setFontSize(option.value)}
                                className={`a11y-panel-button ${settings.fontSize === option.value ? 'active' : ''}`}
                                role="radio"
                                aria-checked={settings.fontSize === option.value}
                                aria-label={option.label}
                              >
                                <Type className="w-4 h-4 mx-auto mb-1" aria-hidden="true" />
                                {option.icon}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Contrast */}
                        <div className="a11y-panel-section">
                          <label id="contrast-label">Contrast</label>
                          <div className="a11y-panel-buttons" role="radiogroup" aria-labelledby="contrast-label">
                            <button
                              onClick={() => setContrast('normal')}
                              className={`a11y-panel-button ${settings.contrast === 'normal' ? 'active' : ''}`}
                              role="radio"
                              aria-checked={settings.contrast === 'normal'}
                            >
                              <Contrast className="w-4 h-4 mx-auto mb-1" aria-hidden="true" />
                              Normal
                            </button>
                            <button
                              onClick={() => setContrast('high')}
                              className={`a11y-panel-button ${settings.contrast === 'high' ? 'active' : ''}`}
                              role="radio"
                              aria-checked={settings.contrast === 'high'}
                            >
                              <Contrast className="w-4 h-4 mx-auto mb-1" aria-hidden="true" />
                              High
                            </button>
                          </div>
                        </div>

                        {/* Reduced Motion */}
                        <div className="a11y-panel-section">
                          <button
                            onClick={toggleReducedMotion}
                            className={`a11y-toggle w-full ${settings.reducedMotion ? 'active' : ''}`}
                            aria-pressed={settings.reducedMotion}
                          >
                            <span className="flex items-center gap-2">
                              <Zap className="w-4 h-4" aria-hidden="true" />
                              Reduce Motion
                            </span>
                            <div className="a11y-toggle-switch" aria-hidden="true" />
                          </button>
                        </div>

                        <p className="text-xs text-gray-500 mt-4">
                          Settings are saved automatically and persist across visits.
                        </p>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/training"
              className="px-4 py-2 rounded-full text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              Training
            </Link>
            <Link
              to="/account"
              data-testid="nav-account-btn"
              className="px-4 py-2 rounded-full text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              Account
            </Link>
            <Link
              to="/shop"
              data-testid="nav-shop-btn"
              className="px-5 py-2 rounded-full bg-white text-black text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              Shop Now
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => {
                setShowA11yPanel(!showA11yPanel);
                announce(showA11yPanel ? 'Accessibility panel closed' : 'Accessibility panel opened');
              }}
              className={`p-2 rounded-full border transition-all ${
                settings.enabled
                  ? 'bg-blue-500 border-blue-500 text-white'
                  : 'border-gray-700 text-gray-400'
              }`}
              aria-label="Accessibility Settings"
              aria-expanded={showA11yPanel}
            >
              <Eye className="w-5 h-5" aria-hidden="true" />
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0a0a0a]/98 backdrop-blur-md border-t border-gray-800/50"
            role="menu"
          >
            <div className="px-6 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  role="menuitem"
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-white bg-white/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/training"
                role="menuitem"
                className="block px-4 py-3 rounded-xl text-base font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                Training
              </Link>
              <Link
                to="/account"
                role="menuitem"
                className="block px-4 py-3 rounded-xl text-base font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                Account
              </Link>
              <Link
                to="/shop"
                role="menuitem"
                className="block px-4 py-3 rounded-xl bg-white text-black text-base font-medium text-center mt-4"
              >
                Shop Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Accessibility Panel */}
      <AnimatePresence>
        {showA11yPanel && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden fixed top-20 left-4 right-4 a11y-panel"
            style={{ position: 'fixed', width: 'auto', maxWidth: 'none' }}
            role="dialog"
            aria-label="Accessibility settings"
          >
            <h3>Accessibility Settings</h3>

            <div className="a11y-panel-section">
              <button
                onClick={toggleAccessibility}
                className={`a11y-toggle w-full ${settings.enabled ? 'active' : ''}`}
                aria-pressed={settings.enabled}
              >
                <span className="font-medium">Enable Accessibility</span>
                <div className="a11y-toggle-switch" aria-hidden="true" />
              </button>
            </div>

            {settings.enabled && (
              <>
                <div className="a11y-panel-section">
                  <label>Text Size</label>
                  <div className="a11y-panel-buttons">
                    {['normal', 'large', 'xl'].map((size) => (
                      <button
                        key={size}
                        onClick={() => setFontSize(size)}
                        className={`a11y-panel-button ${settings.fontSize === size ? 'active' : ''}`}
                        aria-pressed={settings.fontSize === size}
                      >
                        {size === 'normal' ? 'A' : size === 'large' ? 'A+' : 'A++'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="a11y-panel-section">
                  <label>Contrast</label>
                  <div className="a11y-panel-buttons">
                    <button
                      onClick={() => setContrast('normal')}
                      className={`a11y-panel-button ${settings.contrast === 'normal' ? 'active' : ''}`}
                    >
                      Normal
                    </button>
                    <button
                      onClick={() => setContrast('high')}
                      className={`a11y-panel-button ${settings.contrast === 'high' ? 'active' : ''}`}
                    >
                      High
                    </button>
                  </div>
                </div>

                <div className="a11y-panel-section">
                  <button
                    onClick={toggleReducedMotion}
                    className={`a11y-toggle w-full ${settings.reducedMotion ? 'active' : ''}`}
                  >
                    <span>Reduce Motion</span>
                    <div className="a11y-toggle-switch" />
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
